import type dynamicIconImports from 'lucide-react/dynamicIconImports';
import { URLS } from '@/lib/consts.ts';
import { ServiceType, type ServiceURLs } from '../../types';

/*
 * Section Identifiers
 */
export const INCLUDED_SECTION_ID = 'included';
export const SUB_TITLE = `WHAT'S INCLUDED`;
export const CTA_BUTTON_TEXT = `How Do I Get Started?`;

/*
 * Included Section Titles & Descriptions
 */
const INCLUDED_TITLES = {
  [ServiceType.ROOT]: '',
  [ServiceType.BUILD]: 'Fully Managed Design & Build Services',
  [ServiceType.RENOVATE]: 'Fully Managed Design & Renovation Services',
} as const;

const INCLUDED_DESCRIPTIONS = {
  [ServiceType.ROOT]: '',
  [ServiceType.BUILD]:
    'Keep your project on track, finish on budget, and move in on time with our comprehensive design and build services curated by years of experience in the field.',
  [ServiceType.RENOVATE]:
    'Keep your renovation project on track, finish on budget, and achieve the results you desire with our comprehensive design and renovation services.',
} as const;

export type IconName = keyof typeof dynamicIconImports;

interface IncludedItem {
  content: string;
  name: IconName;
  title: string;
}

/*
 * Features List (Common Across Build & Renovate)
 */
export const INCLUDED: IncludedItem[] = [
  {
    content: `From conceptual drawings to construction-ready plans, our in-house design team in Claremore brings your vision to life with creativity and precision. We create detailed floor plans and immersive 3D walkthroughs so you can experience every room before construction begins.`,
    name: 'drafting-compass',
    title: 'Design',
  },
  {
    content: `Ensure your home is properly situated on your land with a meticulously prepared site plan created during our walkthrough of your property. We evaluate terrain, drainage, sun exposure, and views to optimize curb appeal and long-term functionality for your Rogers County building site.`,
    name: 'map-pin',
    title: 'Site Plan',
  },
  {
    content: `We work closely with you to develop and manage a detailed, line-item budget that aligns with your vision and financial goals. Each home has a dedicated budget maintained in-house and updated in real time, tracking every dollar throughout the construction process so there are no surprises.`,
    name: 'filter-x',
    title: 'Budgeting',
  },
  {
    content: `Leave the details to us. Our team handles upcoming material needs by researching options and providing budget-inclusive selections for your review. We leverage our long-standing supplier relationships across northeastern Oklahoma to secure competitive pricing on quality materials.`,
    name: 'clipboard-list',
    title: 'Material Planning',
  },
  {
    content: `We take care of all necessary permits and inspections required by Rogers County, Mayes County, and local municipal authorities. Our familiarity with regional building codes and inspection processes keeps your project moving forward without delays or compliance issues.`,
    name: 'stamp',
    title: 'Permits & Inspections',
  },
  {
    content: `Choose from a wide range of materials, fixtures, and finishes to personalize your home and create a space that reflects your style. We guide you through the selection process with trusted suppliers and provide professional installation, ensuring every detail meets our quality standards.`,
    name: 'swatch-book',
    title: 'Selections & Finishes',
  },
  {
    content: `Cornerstone has built long-lasting homes across Claremore and the Tulsa metro area for over 20 years. With our skilled tradespeople, experienced project management, and proven construction methods tailored to Oklahoma conditions, we bring your vision to life with craftsmanship and attention to detail.`,
    name: 'hammer',
    title: 'Construction',
  },
  {
    content: `Enjoy peace of mind knowing that your project is covered by our comprehensive warranty, providing protection for structural elements, major systems, and workmanship long after construction is complete. We stand behind every home we build.`,
    name: 'badge-check',
    title: 'Warranty',
  },
  {
    content: `Our commitment to excellence does not end when the project is complete. Count on us for ongoing support and assistance with your new home. We are always available to help with additional projects such as pools, fencing, landscaping, and outdoor living spaces. Our reliable resources are beneficial long after move-in day.`,
    name: 'users',
    title: 'Post-Construction Services',
  },
];

/*
 * Type for Included Section Data
 */
export interface IncludedConstants {
  title: string;
  description: string;
}

/*
 * Generates "What's Included" Section Data
 */
function generateIncludedData(serviceType: ServiceType): IncludedConstants {
  return {
    title: INCLUDED_TITLES[serviceType],
    description: INCLUDED_DESCRIPTIONS[serviceType],
  };
}

/*
 * Included Section Content (Mapped by URL)
 */
const ROOT_INCLUDED_SECTION_DATA = generateIncludedData(ServiceType.ROOT);
const BUILD_INCLUDED_SECTION_DATA = generateIncludedData(ServiceType.BUILD);
const RENOVATE_INCLUDED_SECTION_DATA = generateIncludedData(
  ServiceType.RENOVATE,
);

const INCLUDED_SECTION_CONTENT: Record<ServiceURLs, IncludedConstants> = {
  [URLS.SERVICES.ROOT]: ROOT_INCLUDED_SECTION_DATA,
  [URLS.SERVICES.DESIGN_AND_BUILD]: BUILD_INCLUDED_SECTION_DATA,
  [URLS.SERVICES.DESIGN_AND_RENOVATE]: RENOVATE_INCLUDED_SECTION_DATA,
};

export default INCLUDED_SECTION_CONTENT;
